/**
 * MoveToFolderDialog - Modal para mover arquivos/pastas entre pastas
 * 
 * Mostra uma árvore de pastas hierárquica estilo Google Drive.
 * Features:
 * - Navegação hierárquica com indentação visual
 * - Destaque da pasta destino selecionada
 * - Opção de mover para raiz
 * - Tooltips de ajuda
 */

import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  alpha,
  Collapse,
  keyframes,
  useMediaQuery,
  useTheme
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import HomeIcon from '@mui/icons-material/Home'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { TranscriptionFolder } from '../../../types/folder'
import { listAllFolders } from '../../../services/api'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (targetFolderId: string | null) => void
  /** IDs de pastas/arquivos sendo movidos (para desabilitar destinos inválidos) */
  excludeFolderIds?: string[]
  /** Pasta atual dos itens (para destaque) */
  currentFolderId?: string | null
  /** Título customizado */
  title?: string
  /** Descrição do que está sendo movido */
  itemDescription?: string
}

interface FolderNode {
  folder: TranscriptionFolder
  children: FolderNode[]
  depth: number
}

function buildTree(folders: TranscriptionFolder[]): FolderNode[] {
  const map = new Map<string | null, TranscriptionFolder[]>()

  // Agrupa por parentFolderId
  for (const f of folders) {
    const parentId = f.parentFolderId ?? null
    if (!map.has(parentId)) map.set(parentId, [])
    map.get(parentId)!.push(f)
  }

  function buildChildren(parentId: string | null, depth: number): FolderNode[] {
    const children = map.get(parentId) || []
    return children.map(f => ({
      folder: f,
      children: buildChildren(f.id, depth + 1),
      depth
    }))
  }

  return buildChildren(null, 0)
}

export default function MoveToFolderDialog({
  open,
  onClose,
  onConfirm,
  excludeFolderIds = [],
  currentFolderId = null,
  title = 'Mover para pasta',
  itemDescription
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [folders, setFolders] = useState<TranscriptionFolder[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      setLoading(true)
      setSelectedFolderId(null)
      listAllFolders()
        .then(f => {
          setFolders(f)
          // Expande pasta pai da pasta atual
          if (currentFolderId) {
            const current = f.find(fo => fo.id === currentFolderId)
            if (current?.parentFolderId) {
              setExpandedIds(new Set([current.parentFolderId]))
            }
          }
        })
        .catch(() => setFolders([]))
        .finally(() => setLoading(false))
    }
  }, [open, currentFolderId])

  const tree = useMemo(() => buildTree(folders), [folders])
  const excludeSet = useMemo(() => new Set(excludeFolderIds), [excludeFolderIds])

  const toggleExpand = (folderId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const renderNode = (node: FolderNode): React.ReactNode => {
    const isExcluded = excludeSet.has(node.folder.id)
    const isCurrent = node.folder.id === currentFolderId
    const isSelected = node.folder.id === selectedFolderId
    const isExpanded = expandedIds.has(node.folder.id)
    const hasChildren = node.children.length > 0
    const folderColor = node.folder.color || '#90CAF9'

    // Responsive indentation: smaller on mobile to prevent overflow
    const baseIndent = isMobile ? 1 : 2
    const depthMultiplier = isMobile ? 2 : 3

    return (
      <React.Fragment key={node.folder.id}>
        <ListItemButton
          selected={isSelected}
          disabled={isExcluded}
          onClick={() => {
            if (!isExcluded) setSelectedFolderId(node.folder.id)
          }}
          sx={{
            pl: baseIndent + node.depth * depthMultiplier,
            py: { xs: 1, sm: 0.75 },
            borderRadius: 1.5,
            mx: { xs: 0.5, sm: 1 },
            mb: 0.5,
            opacity: isExcluded ? 0.4 : 1,
            bgcolor: isSelected ? alpha(folderColor, 0.12) : 'transparent',
            border: isSelected ? `1px solid ${alpha(folderColor, 0.4)}` : '1px solid transparent',
            '&:hover': {
              bgcolor: alpha(folderColor, 0.08)
            }
          }}
        >
          {/* Expand/Collapse toggle */}
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); toggleExpand(node.folder.id) }}
              sx={{ mr: 0.5, p: 0.25, minWidth: { xs: 24, sm: 28 } }}
            >
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: { xs: 24, sm: 28 }, mr: 0.5 }} />
          )}

          <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
            {isExpanded && hasChildren ? (
              <FolderOpenIcon sx={{ color: folderColor, fontSize: { xs: 20, sm: 24 } }} />
            ) : (
              <FolderIcon sx={{ color: folderColor, fontSize: { xs: 20, sm: 24 } }} />
            )}
          </ListItemIcon>

          <ListItemText
            primary={node.folder.name}
            secondary={isCurrent ? '(local atual)' : undefined}
            primaryTypographyProps={{
              fontWeight: isSelected ? 700 : 500,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              noWrap: true
            }}
            secondaryTypographyProps={{
              fontSize: '0.7rem',
              color: 'text.disabled'
            }}
          />

          {node.folder.itemCount != null && node.folder.itemCount > 0 && !isMobile && (
            <Typography variant="caption" sx={{ color: 'text.disabled', mr: 1 }}>
              {node.folder.itemCount}
            </Typography>
          )}
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isExpanded}>
            {node.children.map(renderNode)}
          </Collapse>
        )}
      </React.Fragment>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          animation: `${fadeIn} 0.2s ease-out`,
          maxHeight: isMobile ? '100vh' : '70vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 }, pt: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
            <Box
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#1976d2', 0.1),
              }}
            >
              <DriveFileMoveIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 22 } }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                {title}
              </Typography>
              {itemDescription && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {itemDescription}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, minHeight: { xs: 'auto', sm: 200 }, flex: 1 }}>
        {/* Dica */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, bgcolor: alpha('#1976d2', 0.04) }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: { xs: 12, sm: 14 }, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {isMobile ? 'Selecione o destino' : 'Selecione a pasta de destino ou escolha "Raiz" para mover para o início'}
            </Typography>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <List dense sx={{ py: 1 }}>
            {/* Opção Raiz */}
            <ListItemButton
              selected={selectedFolderId === null}
              onClick={() => setSelectedFolderId(null)}
              sx={{
                py: { xs: 1, sm: 0.75 },
                borderRadius: 1.5,
                mx: { xs: 0.5, sm: 1 },
                mb: 0.5,
                bgcolor: selectedFolderId === null ? alpha('#1976d2', 0.08) : 'transparent',
                border: selectedFolderId === null ? `1px solid ${alpha('#1976d2', 0.3)}` : '1px solid transparent',
                '&:hover': { bgcolor: alpha('#1976d2', 0.06) }
              }}
            >
              <Box sx={{ width: { xs: 24, sm: 28 }, mr: 0.5 }} />
              <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                <HomeIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
              </ListItemIcon>
              <ListItemText
                primary={isMobile ? 'Raiz' : 'Minhas transcrições (raiz)'}
                primaryTypographyProps={{
                  fontWeight: selectedFolderId === null ? 700 : 500,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' }
                }}
              />
            </ListItemButton>

            {/* Árvore de pastas */}
            {tree.map(renderNode)}

            {tree.length === 0 && !loading && (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                <FolderIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: '#e0e0e0', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Nenhuma pasta criada ainda
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  Crie pastas para organizar suas transcrições
                </Typography>
              </Box>
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
          '& > button': { width: { xs: '100%', sm: 'auto' } }
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ minHeight: { xs: 44, sm: 36 } }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(selectedFolderId)}
          startIcon={<DriveFileMoveIcon />}
          sx={{ px: { xs: 2, sm: 3 }, minHeight: { xs: 48, sm: 36 } }}
        >
          {isMobile ? 'Mover' : `Mover para ${selectedFolderId === null ? 'Raiz' : 'pasta'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
